import { Tooltip } from "@mui/material";
import { flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useMemo } from "react";
import { formatInTimeZone } from "date-fns-tz";

import { StandardDataTimeSeries } from "@/lib/api";
import UnitRenderer from "@/lib/helper/UnitRenderer";
import { useSelectedTimeRange } from "@/features/dashboard/store";
import { useWidgetTimezone } from "@/features/shared/widget-context/widget-runtime-context";

import { DataTableStyled } from "./datatable.styles";

interface DataTableProps {
  data: StandardDataTimeSeries[];
}

const DataTable = ({ data }: DataTableProps) => {
  const tz = useWidgetTimezone();
  const timezone = tz?.name === "UTC" ? tz.offset : (tz?.name ?? "");

  const timeRange = useSelectedTimeRange();
  const mappedData = useMemo(() => {
    const dataMap = new Map();

    const roundingPrecision = (value: number, unit: string) => {
      if (typeof value === "number" && value % 1 !== 0) {
        if (unit === "knots") {
          return (Math.round(value * 10) / 10).toString();
        }
      }
      return value.toString();
    };

    data.forEach((curr) => {
      curr.datapoints.forEach((dataCurr) => {
        const { timestamp } = dataCurr;
        const time = new Date(timestamp);
        if (time < timeRange.timeStart || time > timeRange.timeEnd) return;

        const parameterId = curr.parameter.id;

        if (!dataMap.has(timestamp)) {
          dataMap.set(timestamp, {
            timestamp: formatInTimeZone(time, timezone, "dd-MM-yyyy HH:mm"),
          });
        }
        if (dataCurr.value) {
          dataMap.get(timestamp)[parameterId] = UnitRenderer(
            roundingPrecision(dataCurr.value, curr.parameter.unit.type),
            {
              ...curr.parameter.unit,
              type:
                curr.parameter.unit.type === "degree"
                  ? "default"
                  : curr.parameter.unit.type,
            },
          );
        }
      });
    });

    return [...dataMap.values()];
  }, [data, timeRange]);

  const columns = useMemo(() => {
    return [
      {
        header: "Timestamp",
        id: "timestamp",
        accessorKey: "timestamp",
      },
      ...data.map((d) => ({
        header: () => (
          <Tooltip title={d.parameter.description}>
            <span>{d.parameter.id}</span>
          </Tooltip>
        ),
        id: d.parameter.id,
        accessorKey: d.parameter.id,
      })),
    ];
  }, [data]);

  const table = useReactTable({
    columns,
    data: mappedData,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <DataTableStyled cellSpacing={0}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <th key={header.id}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </th>
            ))}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => (
          <tr key={row.id}>
            {row.getVisibleCells().map((cell) => (
              <td key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </DataTableStyled>
  );
};

export default DataTable;
